import { useState } from "react";
import useSWR from "swr";
import * as Sentry from "@sentry/react";
import { useApiClient } from "@/hooks/useApiClient";
import {
  postDataTransfer,
  DATA_TRANSFERS_ENDPOINT,
} from "@/features/salesforceIntegration/api/postDataTransfer";
import { getDataTransfer } from "@/features/salesforceIntegration/api/getDataTransfer";
import {
  type GetDataTransferResponse,
  type LatestTransfer,
  type TransferStatus,
} from "@/features/salesforceIntegration/types";

/** ポーリング間隔（実装計画書7.4節で確定した5秒）。 */
const POLLING_INTERVAL_MS = 5000;

const isSettled = (status: TransferStatus | undefined): boolean =>
  status === "SUCCEEDED" || status === "FAILED";

/**
 * 直近ジョブの詳細をポーリング対象にするか。実行中はもちろん、FAILEDも対象に含める。
 * 失敗理由（`errorMessage`）は判定APIに含まれず、詳細APIを1回引かないと
 * リロード後にツールチップが空になるため（実装計画書7.4節）。SUCCEEDEDは理由が不要で含めない。
 */
const shouldTrackTransfer = (latestTransfer: LatestTransfer): boolean =>
  !isSettled(latestTransfer.status) || latestTransfer.status === "FAILED";

/** 受け付けたジョブ。依頼IDをキーに保持する。 */
interface PostedTransfer {
  transferId: number;
  status: TransferStatus;
}

export interface UseSalesforceDataTransferArgs {
  /**
   * Salesforce連携が有効か。無効ならポーリングを行わない。
   * 実行中に無効化された場合、画面の追跡は止めるがサーバーのジョブは動き続ける
   * （キャンセルはしない）。再び有効になれば判定APIの`latestTransfer`から追跡を再開する。
   */
  isEnabled: boolean;
  pictureRequestId: number;
  /** 判定APIが返す直近の連携状況。ページ読み込み時点で進行中ならポーリングを再開する。 */
  latestTransfer: LatestTransfer | null;
  /** 連携が完了・失敗した際に呼ぶ（判定APIの`mutate`を想定）。 */
  onSettled: () => void;
}

export interface UseSalesforceDataTransferResult {
  /** 直近の連携ステータス。未実施なら`null`。 */
  status: TransferStatus | null;
  /** 失敗時の理由（ツールチップ表示用）。 */
  errorMessage: string | null;
  /** 受け付け中またはポーリング中かどうか。ボタンの二重送信防止に使う。 */
  isTransferring: boolean;
  /** 連携を開始する。`uniqueIds`省略時はリクエストの全物件が対象。 */
  transfer: (uniqueIds?: string[]) => Promise<void>;
}

/**
 * Salesforce連携の受け付けと、完了までのポーリングを担うhook。
 *
 * 202レスポンスの`transferId`を保持し、`GET /salesforce/data-transfers/{transferId}/`を
 * 5秒間隔でポーリングする。`SUCCEEDED`/`FAILED`に到達したらポーリングを止め、
 * `onSettled`（判定APIの`mutate`）を呼んで表示を最新化する（実装計画書7.4節）。
 *
 * 保持する状態はすべて依頼単位で扱う。詳細画面は`/acquirebookstatus/:id`を
 * 切り替えても同じコンポーネントのままなので、依頼Aの実行中に依頼Bへ移ると
 * Aのジョブを追い続け、Bのメニューが不当に非活性になる。
 */
export const useSalesforceDataTransfer = (
  args: UseSalesforceDataTransferArgs
): UseSalesforceDataTransferResult => {
  const { isEnabled, pictureRequestId, latestTransfer, onSettled } = args;
  const { apiClient } = useApiClient();

  // 受け付け直後に自分が起こしたジョブ。リロードで失われても下の派生値が引き継ぐ。
  // 依頼IDごとに保持するのは、依頼Aの遅い応答が依頼Bの追跡先を消さないようにするため。
  // 単一の値で持つと、B→Aの順に応答が返ったときAがBを上書きしてBのポーリングが止まる
  const [postedTransferByRequest, setPostedTransferByRequest] = useState<
    Readonly<Record<number, PostedTransfer | undefined>>
  >({});

  const postedForCurrentRequest =
    postedTransferByRequest[pictureRequestId] ?? null;

  // 実行中・失敗したジョブは判定APIから遅れて届くため、状態に持たず毎レンダーで導出する。
  // useStateの初期値で評価すると初回レンダーの値（必ずnull）で固定され、
  // リロード後に追跡すべき連携がポーリングされなくなる
  const activeTransferId = !isEnabled
    ? null
    : postedForCurrentRequest?.transferId ??
      (latestTransfer && shouldTrackTransfer(latestTransfer)
        ? latestTransfer.transferId
        : null);

  // 受け付け中の依頼。SWRのミューテーション状態は依頼をまたいでも残るため、
  // 「どの依頼のPOSTが飛んでいるか」は自前で持つ。複数の依頼で同時に飛びうるので集合で扱う
  const [pendingPostRequestIds, setPendingPostRequestIds] = useState<
    ReadonlySet<number>
  >(new Set());

  const { data: polled } = useSWR<GetDataTransferResponse>(
    activeTransferId !== null
      ? `${DATA_TRANSFERS_ENDPOINT}${activeTransferId}/`
      : null,
    async () => await getDataTransfer(apiClient, activeTransferId as number),
    {
      refreshInterval: (latest) =>
        latest && isSettled(latest.status) ? 0 : POLLING_INTERVAL_MS,
      onSuccess: (latest) => {
        if (isSettled(latest.status)) {
          onSettled();
        }
      },
    }
  );

  const transfer = async (uniqueIds?: string[]): Promise<void> => {
    // 受け付けた時点の依頼を覚える。応答が遅れて別の依頼を見ている間に返っても、
    // この値が一致しなければ描画時に捨てられる
    const requestedFor = pictureRequestId;
    setPendingPostRequestIds((previous) => new Set(previous).add(requestedFor));
    try {
      const result = await postDataTransfer(apiClient, {
        pictureRequestId: requestedFor,
        uniqueIds,
      });
      setPostedTransferByRequest((previous) => ({
        ...previous,
        [requestedFor]: {
          transferId: result.transferId,
          status: result.status,
        },
      }));
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setPendingPostRequestIds((previous) => {
        if (!previous.has(requestedFor)) return previous;
        const next = new Set(previous);
        next.delete(requestedFor);
        return next;
      });
    }
  };

  const status =
    polled?.status ??
    postedForCurrentRequest?.status ??
    latestTransfer?.status ??
    null;

  return {
    status,
    errorMessage: polled?.errorMessage ?? null,
    isTransferring:
      isEnabled &&
      (pendingPostRequestIds.has(pictureRequestId) ||
        (activeTransferId != null && !isSettled(status ?? undefined))),
    transfer,
  };
};
